import isNumber from 'lodash/isNumber';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  DragAndDropEditorModalType,
  useDragAndDropEditorContext,
  useDragAndDropEditorModalsContext,
} from 'modules/dragAndDropEditor/context';
import { useDragAndDropEditorTranslations } from 'modules/dragAndDropEditor/hooks/useDragAndDropEditorTranslations';
import { SavedRow } from 'modules/dragAndDropEditor/types';
import { useEffect } from 'react';
import { SlideOver } from 'ui/slideOver';
import { SlideOverHeader } from 'ui/slideOver/SlideOver.styled';
import { fontWeights, heading4, subtleShadow } from 'ui/theme';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import noop from 'utils/noop';
import {
  CreateOrEditSavedRowFormValues,
  withCreateOrEditSavedRowFormProvider,
} from './CreateOrEditSavedRowFormProvider';
import { CreateOrEditSavedRowSlideOverBody } from './CreateOrEditSavedRowSlideOverBody';
import { CreateOrEditSavedRowSlideOverFooter } from './CreateOrEditSavedRowSlideOverFooter';

const HeaderTitle = styled.span`
  ${heading4};
  font-weight: ${fontWeights.bold};
`;

const StyledSlideOver = styled(SlideOver)`
  && ${SlideOverHeader} {
    ${subtleShadow};
  }
`;

export const SAVED_ROW_SLIDE_OVER_ID = 'saved-row-slide-over';

export type CreateOrEditSavedRowSlideOverProps = {
  /** The saved row being edited's ID */
  savedRow?: SavedRow;
  /** Callback to run after closing the slideover */
  onCloseCallback?: () => void;
  /** Callback to run when fail to edit a bee row */
  onRejectEditBeeRow?: () => void;
  /** Callback to run when succeeded editing a bee row */
  onResolveEditBeeRow?: (args: any) => void;
};

export const CreateOrEditSavedRowSlideOver = withCreateOrEditSavedRowFormProvider(
  ({
    savedRow,
    onCloseCallback = noop,
    onRejectEditBeeRow = noop,
    onResolveEditBeeRow = noop,
  }: CreateOrEditSavedRowSlideOverProps) => {
    const { createSavedRowReset, resetUpdateSavedRow, getSavedRowCount } =
      useDragAndDropEditorContext();
    const { closeModal, openModal } = useDragAndDropEditorModalsContext();

    const { formState } = useFormContext<CreateOrEditSavedRowFormValues>();

    const isEditingRow = isNumber(savedRow?.id);

    useEffect(() => {
      if (!isEditingRow) {
        getSavedRowCount();
      }
    }, [isEditingRow, getSavedRowCount]);

    const handleClose = () => {
      onRejectEditBeeRow();
      closeModal(DragAndDropEditorModalType.CreateOrEditSavedRow);

      // clear potential server-side failure data
      createSavedRowReset();
      resetUpdateSavedRow();
      onCloseCallback();
    };

    const onRequestClose = (close: () => void) => {
      if (formState.isDirty) {
        // unsaved changes confirmation
        openModal(DragAndDropEditorModalType.SavedRowUnsavedChanges, {
          onClose: () => {
            closeModal(DragAndDropEditorModalType.SavedRowUnsavedChanges);
          },
          onLeaveWithoutSaving: () => {
            closeModal(DragAndDropEditorModalType.SavedRowUnsavedChanges);
            close();
          },
        });
      } else {
        close();
      }
    };

    const { ct } = useDragAndDropEditorTranslations();
    useUnsavedChangesPrompt(formState.isDirty, ct('errorMessage.unsavedPrompt')); // triggered when the user tries to leave the page with unsaved changes

    return (
      <StyledSlideOver
        isOpen
        withPortal
        id={SAVED_ROW_SLIDE_OVER_ID}
        data-test={SAVED_ROW_SLIDE_OVER_ID}
        onClose={handleClose}
        onClickOutside={handleClose}
        onRequestClose={onRequestClose}
        headerContent={<HeaderTitle>Save row</HeaderTitle>}
        bodyContent={
          <CreateOrEditSavedRowSlideOverBody
            savedRow={savedRow}
            isEditingRow={isEditingRow}
            handleClose={handleClose}
            onRequestClose={onRequestClose}
          />
        }
        footerContent={
          <CreateOrEditSavedRowSlideOverFooter
            savedRow={savedRow}
            isEditingRow={isEditingRow}
            onCloseConfirmation={() => onRequestClose(handleClose)}
            onRejectEditBeeRow={onRejectEditBeeRow}
            onResolveEditBeeRow={onResolveEditBeeRow}
            onCloseCallback={onCloseCallback}
          />
        }
      />
    );
  },
);
